import { pxToRem } from '@fluentsms/agentnet-web-components';
import {
  Box,
  Checkbox,
  createStyles,
  FormControlLabel,
  Grid,
  makeStyles,
  Switch,
  Theme,
  Tooltip,
  Typography,
} from '@material-ui/core';
import InfoIcon from '@material-ui/icons/Info';
import { isValid, parse } from 'date-fns';
import { FincenReportContext, FincenReportContextInterface } from 'hooks/FincenReportContext';
import type { ChangeEvent, FC } from 'react';
import { useContext, useEffect, useState } from 'react';

import RadioGroup from 'ui-kit/components/radios/RadioGroup';
import { AddressFields } from 'ui-kit/form/interfaces/AddressModal';
import { AgentNetDropdownSelector } from 'ui-kit/inputs';
import DateFieldString from 'ui-kit/inputs/DateField/DateFieldString';
import territories from 'ui-kit/inputs/geo-data/us-territory.json';
import canadaProvinces from 'ui-kit/inputs/geo-data/canada-provinces.json';
import mexicoStates from 'ui-kit/inputs/geo-data/mexico-states.json';
import foreignTerritories from 'ui-kit/inputs/geo-data/foreign-territory.json';
import GoogleAutocompleteSearch from 'ui-kit/inputs/GoogleAutocompleteSearch/GoogleAutocompleteSearch';
import states from 'ui-kit/inputs/StateSelectField/states.json';
import { AgentNetTextInput } from 'ui-kit/inputs/TextField';
import { CountryCode, isUS, requiresStateAndPostalCode, isUSTerritory } from '@agentnet/utils';
import USPostalCodeField from 'ui-kit/inputs/USPostalCodeField/USPostalCodeField';
import { FieldValidationError } from 'utilities/validation/validation';
import { PartyType } from 'utilities/validation/schemas/fincen-transferee-schema';
import { IdentificationNumberField } from '../../common/IdentificationNumberField';
import { TransfereeParty } from '../../types';
import {
  addressTypeOptions,
  authorizationCapacityOptions,
  baseIdentificationTypeOptions,
  beneficialOwnerCategoryOptions,
  foreignTerritoryOptions,
  TransfereeType,
} from '../constants';
import { PartyFormProps } from '../types';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    formContainer: {
      padding: 0,
      width: '100%',
      marginLeft: 0,
      marginBottom: 0,
      marginRight: 0,
    },

    radioGroupContainer: {
      marginTop: theme.spacing(0),
      paddingLeft: theme.spacing(1),
      marginBottom: theme.spacing(2),
    },
    sectionContainer: {
      marginTop: theme.spacing(3),
      marginBottom: theme.spacing(3),
    },
    sectionHeader: {
      fontSize: pxToRem(16),
      fontWeight: 500,
      marginBottom: theme.spacing(2),
      // Align headers with the grid item left padding (common is 8-12px). Use 8px to match address sections (spacing=2).
      paddingLeft: theme.spacing(1),
    },

    switch: {
      marginRight: theme.spacing(1),
    },
    infoIcon: {
      fontSize: pxToRem(16),
      marginLeft: theme.spacing(1),
      color: theme.palette.primary.main,
      verticalAlign: 'middle',
    },
    addressContainer: {
      marginTop: theme.spacing(1),
    },
    errorMessage: {
      marginLeft: 14,
      marginTop: 3,
      display: 'block',
    },
    dropdownError: {
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: `${theme.palette.error.main} !important`,
      },
    },
    minorCheckboxLabel: {
      paddingLeft: theme.spacing(1),
      marginBottom: theme.spacing(1.5),
    },
  }),
);

const PartyForm: FC<PartyFormProps> = ({
  party,
  transfereeIndex,
  transfereeType,
  partyIndex,
  onUpdate,
  validationErrors: contextValidationErrors,
  clearValidationErrors,
}) => {
  const classes = useStyles();
  const fincenReportCtx = useContext<FincenReportContextInterface | null>(FincenReportContext);
  const { generateButtonClicked, setTransfereeValidationErrors } = fincenReportCtx ?? {};

  const [formData, setFormData] = useState<TransfereeParty>(() => {
    const initialData = {
      ...party,
      partyType: party.partyType || 'beneficialOwner',
      alternateNames: party.alternateNames && party.alternateNames.length > 0 ? party.alternateNames : [''],
    };

    // For TRUSTEE_LEGAL_ENTITY: if foreignBusinessNoUSLocation is false and addressType is empty, default to 'foreign'
    if (
      initialData.partyType === PartyType.TRUSTEE_LEGAL_ENTITY &&
      !initialData.foreignBusinessNoUSLocation &&
      !initialData.addressType
    ) {
      initialData.addressType = 'foreign';
      // Ensure address object exists
      if (!initialData.address) {
        initialData.address = {
          address1: '',
          address2: '',
          city: '',
          state: '',
          country: CountryCode.US,
          postalCode: '',
        };
      }
    }

    return initialData;
  });
  const [validationErrors, setValidationErrors] = useState<FieldValidationError[]>([]);
  const [formSubmitted, setFormSubmitted] = useState<boolean>(false);

  const effectiveValidationErrors =
    contextValidationErrors && contextValidationErrors.length > 0 ? contextValidationErrors : validationErrors;

  const clearFieldError = (fieldName: string) => {
    if (!setTransfereeValidationErrors) {
      return;
    }

    // Use whichever error source has errors
    const errorsToFilter =
      contextValidationErrors && contextValidationErrors.length > 0 ? contextValidationErrors : validationErrors || [];

    const filteredErrors = errorsToFilter.filter((error) => {
      // Generate all possible field name patterns to match
      const patterns = [
        `[${transfereeIndex}].parties[${partyIndex}].${fieldName}`,
        `parties[${partyIndex}].${fieldName}`,
        fieldName,
      ];

      // Check if error matches any pattern
      const matches = patterns.some(
        (pattern) =>
          error.field === pattern ||
          error.field.endsWith(`.${fieldName}`) ||
          error.field.includes(`parties[${partyIndex}].${fieldName}`),
      );

      return !matches;
    });

    if (filteredErrors.length !== errorsToFilter.length) {
      setTransfereeValidationErrors(filteredErrors);
    }
  };

  // Sync formData when party prop changes and apply defaults when needed
  useEffect(() => {
    const updatedParty = { ...party };
    let hasChanges = false;

    // Apply default partyType if not set
    if (!updatedParty.partyType) {
      updatedParty.partyType = 'beneficialOwner';
      hasChanges = true;
    }

    // Ensure alternateNames always has at least one empty string
    if (!updatedParty.alternateNames?.length) {
      updatedParty.alternateNames = [''];
      hasChanges = true;
    }

    // Apply default addressType for TRUSTEE_LEGAL_ENTITY when conditions are met
    if (
      updatedParty.partyType === PartyType.TRUSTEE_LEGAL_ENTITY &&
      !updatedParty.foreignBusinessNoUSLocation &&
      !updatedParty.addressType
    ) {
      updatedParty.addressType = 'foreign';
      // Ensure address object exists
      if (!updatedParty.address) {
        updatedParty.address = {
          address1: '',
          address2: '',
          city: '',
          state: '',
          country: CountryCode.US,
          postalCode: '',
        };
      }
      hasChanges = true;
    }

    if (hasChanges) {
      onUpdate(updatedParty, transfereeIndex, partyIndex);
    }

    setFormData(updatedParty);
  }, [party, transfereeIndex, partyIndex, onUpdate]);

  // Clear state error when country is not US
  useEffect(() => {
    if (formData.address?.country && formData.address.country !== CountryCode.US) {
      clearFieldError('address.state');
    }
  }, [formData.address?.country]);

  const addressStateConstraint =
    isUS(formData.address?.country) && formData.address?.state ? formData.address.state : undefined;

  const getFieldError = (fieldName: string): FieldValidationError | undefined => {
    // Only show inline validation errors after Generate Report button is clicked
    if (!generateButtonClicked) {
      return undefined;
    }

    if (fieldName === 'dateOfBirth') {
      return effectiveValidationErrors?.find(
        (err: FieldValidationError) =>
          err.field === `[${transfereeIndex}].parties[${partyIndex}].${fieldName}` ||
          err.field === `parties[${partyIndex}].${fieldName}` ||
          err.field === `transferees[${transfereeIndex}].parties[${partyIndex}].${fieldName}`,
      );
    }

    const fieldToMatch = `[${transfereeIndex}].parties[${partyIndex}].${fieldName}`;
    const exactMatch = effectiveValidationErrors?.find((err: FieldValidationError) => err.field === fieldToMatch);
    if (exactMatch) {
      return exactMatch;
    }

    const schemaFieldToMatch = `parties[${partyIndex}].${fieldName}`;
    return effectiveValidationErrors?.find((err: FieldValidationError) => err.field === schemaFieldToMatch);
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked, type } = e.target;
    const updatedFormData = {
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    };
    setFormData(updatedFormData);

    clearFieldError(name);
  };

  const handleFieldChange = (field: string, value: string | Date | null) => {
    let updatedFormData = {
      ...formData,
      [field]: value,
    };

    if (field === 'identificationType') {
      updatedFormData = {
        ...updatedFormData,
        identificationNumber: '',
      };
    }

    // When changing partyType, set appropriate defaults
    if (field === 'partyType') {
      updatedFormData = {
        ...updatedFormData,
        // Default to 'foreign' (Principal Place of Business U.S.) when selecting TRUSTEE_LEGAL_ENTITY
        addressType: value === PartyType.TRUSTEE_LEGAL_ENTITY ? 'foreign' : '',
        foreignBusinessNoUSLocation: false,
        // Ensure address object exists
        address: updatedFormData.address || {
          address1: '',
          address2: '',
          city: '',
          state: '',
          country: CountryCode.US,
          postalCode: '',
        },
      };
    }

    setFormData(updatedFormData);
    onUpdate(updatedFormData, transfereeIndex, partyIndex);

    clearFieldError(field);
  };

  const handleDateOfBirthChange = (date: string | null) => {
    const updatedFormData = {
      ...formData,
      dateOfBirth: date || '',
    };
    setFormData(updatedFormData);
    onUpdate(updatedFormData, transfereeIndex, partyIndex);

    if (date && date.trim() !== '') {
      const isPartialDate = date.includes('_');

      if (isPartialDate) {
        if (setTransfereeValidationErrors) {
          const newError: FieldValidationError = {
            field: `transferees[${transfereeIndex}].parties[${partyIndex}].dateOfBirth`,
            message: 'Invalid date',
          };
          const updatedErrors = [
            ...(contextValidationErrors || []).filter(
              (err) => err.field !== `transferees[${transfereeIndex}].parties[${partyIndex}].dateOfBirth`,
            ),
            newError,
          ];
          setTransfereeValidationErrors(updatedErrors);
        }
      } else {
        const parsedDate = parse(date, 'MM/dd/yyyy', new Date());
        if (!isValid(parsedDate)) {
          if (setTransfereeValidationErrors) {
            const newError: FieldValidationError = {
              field: `transferees[${transfereeIndex}].parties[${partyIndex}].dateOfBirth`,
              message: 'Invalid date',
            };
            const updatedErrors = [
              ...(contextValidationErrors || []).filter(
                (err) => err.field !== `transferees[${transfereeIndex}].parties[${partyIndex}].dateOfBirth`,
              ),
              newError,
            ];
            setTransfereeValidationErrors(updatedErrors);
          }
        } else {
          const parts = date.split('/');
          if (parts.length === 3) {
            const month = parseInt(parts[0], 10);
            const day = parseInt(parts[1], 10);
            const year = parseInt(parts[2], 10);

            if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1900 || year > 2100) {
              if (setTransfereeValidationErrors) {
                const newError: FieldValidationError = {
                  field: `transferees[${transfereeIndex}].parties[${partyIndex}].dateOfBirth`,
                  message: 'Invalid date',
                };
                const updatedErrors = [
                  ...(contextValidationErrors || []).filter(
                    (err) => err.field !== `transferees[${transfereeIndex}].parties[${partyIndex}].dateOfBirth`,
                  ),
                  newError,
                ];
                setTransfereeValidationErrors(updatedErrors);
              }
            } else {
              clearFieldError('dateOfBirth');
            }
          } else {
            clearFieldError('dateOfBirth');
          }
        }
      }
    } else {
      clearFieldError('dateOfBirth');
    }
  };

  const handleNestedChange = (parentField: keyof TransfereeParty, childField: string, value: string | boolean) => {
    const updatedFormData = {
      ...formData,
      [parentField]: {
        ...(formData[parentField] as AddressFields | Record<string, string | boolean>),
        [childField]: value,
      },
    };
    setFormData(updatedFormData);
    onUpdate(updatedFormData, transfereeIndex, partyIndex);

    clearFieldError(`${String(parentField)}.${childField}`);
  };

  const handleStateChange = (value: string) => {
    const updatedFormData = {
      ...formData,
      address: {
        ...formData.address,
        state: value,
      },
    };
    setFormData(updatedFormData);
    onUpdate(updatedFormData, transfereeIndex, partyIndex);

    clearFieldError('address.state');
  };

  const handleCountryChange = (value: string) => {
    const countryUpper = value?.toUpperCase();
    // Auto-set state for US Territories, reset for other countries
    const newState = isUSTerritory(countryUpper) ? countryUpper : '';
    const updatedFormData = {
      ...formData,
      address: {
        ...formData.address,
        country: value,
        state: newState,
        postalCode: requiresStateAndPostalCode(countryUpper) ? formData.address?.postalCode || '' : '',
      },
    };
    setFormData(updatedFormData);
    onUpdate(updatedFormData, transfereeIndex, partyIndex);

    clearFieldError('address.country');
    if (!requiresStateAndPostalCode(countryUpper)) {
      clearFieldError('address.state');
      clearFieldError('address.postalCode');
    }
  };

  const handleForeignCountryChange = (value: string) => {
    const countryUpper = value?.toUpperCase();
    // Auto-set state for US Territories, reset for other countries
    const newState = isUSTerritory(countryUpper) ? countryUpper : '';
    const newFormData = {
      ...formData,
      foreignPrincipalAddress: {
        ...formData.foreignPrincipalAddress,
        country: value,
        state: newState,
        postalCode: requiresStateAndPostalCode(countryUpper) ? formData.foreignPrincipalAddress?.postalCode || '' : '',
      },
    };
    setFormData(newFormData);
    onUpdate(newFormData, transfereeIndex, partyIndex);

    clearFieldError('foreignPrincipalAddress.country');
    if (!requiresStateAndPostalCode(countryUpper)) {
      clearFieldError('foreignPrincipalAddress.state');
      clearFieldError('foreignPrincipalAddress.postalCode');
    }
  };

  const handleDeleteAlternateName = (idx: number) => {
    const updatedAlternateNames = (formData.alternateNames || []).filter((_, i) => i !== idx);

    if (setTransfereeValidationErrors && contextValidationErrors) {
      const filteredErrors = contextValidationErrors.filter((error) => !error.field.includes(`alternateNames[`));
      setTransfereeValidationErrors(filteredErrors);
    }

    const updatedFormData = {
      ...formData,
      alternateNames: updatedAlternateNames,
    };
    setFormData(updatedFormData);
    onUpdate(updatedFormData, transfereeIndex, partyIndex);
  };

  const territoryOptions = territories.map((territory) => ({
    value: territory.abbreviation,
    label: territory.name,
    name: territory.name,
  }));

  const stateOptions = states.map((state) => ({
    value: state.abbreviation,
    label: state.name,
    name: state.abbreviation,
  }));

  const canadaProvinceOptions = canadaProvinces.map((province) => ({
    name: province.abbreviation,
    value: province.abbreviation,
  }));

  const mexicoStateOptions = mexicoStates.map((state) => ({
    name: state.abbreviation,
    value: state.abbreviation,
  }));

  // Merge all countries for dropdown and remove duplicates
  const allCountryOptions = [
    ...territories.map((t) => ({ name: t.name, value: t.abbreviation })),
    ...foreignTerritories.map((t) => ({ name: t.name, value: t.abbreviation })),
  ]
    .filter((option, index, self) => index === self.findIndex((o) => o.value === option.value))
    .sort((a, b) => {
      const priorityOrder = [
        'United States',
        'American Samoa',
        'Guam',
        'Northern Mariana Islands',
        'Puerto Rico',
        'U.S. Virgin Islands',
        'Canada',
        'Mexico',
      ];
      const aIndex = priorityOrder.indexOf(a.name);
      const bIndex = priorityOrder.indexOf(b.name);
      if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;
      return a.name.localeCompare(b.name);
    });

  // Determine which state/province options to show based on country
  const getStateOptions = (country?: string) => {
    const countryUpper = country?.toUpperCase();
    if (countryUpper === 'US') {
      return stateOptions;
    } else if (isUSTerritory(countryUpper)) {
      return territoryOptions.filter((t) => t.value === countryUpper);
    } else if (countryUpper === 'CA') {
      return canadaProvinceOptions;
    } else if (countryUpper === 'MX') {
      return mexicoStateOptions;
    }
    return [];
  };

  const getStateLabel = (country?: string) => {
    const countryUpper = country?.toUpperCase();
    if (countryUpper === 'US' || isUSTerritory(countryUpper)) {
      return 'State/U.S. Territory';
    } else if (countryUpper === 'CA') {
      return 'Province/Territory';
    } else if (countryUpper === 'MX') {
      return 'State';
    }
    return 'State/Province';
  };

  // Determine which identification type options to use based on party type
  const getIdentificationTypeOptions = (partyType: string) => {
    if (partyType === PartyType.TRUSTEE_LEGAL_ENTITY) {
      return baseIdentificationTypeOptions.filter((option) => option.value !== 'foreignPassport');
    } else {
      return baseIdentificationTypeOptions.filter((option) => option.value !== 'foreignEntityRegistrationNumber');
    }
  };

  return (
    <div className={classes.formContainer}>
      <div className={classes.radioGroupContainer}>
        <RadioGroup
          label="Party Type"
          required
          name="partyType"
          selected={{ value: formData.partyType }}
          onChange={(e) => handleFieldChange('partyType', e.target.value)}
          row
          border
          options={[
            {
              value: 'beneficialOwner',
              label: 'Beneficial Owner',
              dataTestId: `fincen-party-${transfereeIndex}-${partyIndex}-type-beneficial-owner-radio`,
            },
            {
              value: 'signingIndividual',
              label: 'Signing Individual',
              dataTestId: `fincen-party-${transfereeIndex}-${partyIndex}-type-signing-individual-radio`,
            },
            {
              value: PartyType.TRUSTEE_LEGAL_ENTITY,
              label: 'Trustee that is a Legal Entity',
              dataTestId: `fincen-party-${transfereeIndex}-${partyIndex}-type-trustee-legal-entity-radio`,
            },
            {
              value: 'bothBeneficialOwnerAndSigningIndividual',
              label: 'Both Beneficial Owner and Signing Individual',
              dataTestId: `fincen-party-${transfereeIndex}-${partyIndex}-type-both-radio`,
            },
          ]}
        />
      </div>

      {formData.partyType === PartyType.TRUSTEE_LEGAL_ENTITY && (
        <div className={classes.sectionContainer}>
          <Typography variant="h6" className={classes.sectionHeader}>
            Entity Information
          </Typography>
          <Grid container spacing={2} style={{ width: '50%' }}>
            <Grid item xs={12}>
              <AgentNetTextInput
                label="Entity Legal Name"
                name="entityLegalName"
                value={formData.entityLegalName || ''}
                variant="outlined"
                onChange={(e) => handleFieldChange('entityLegalName', e.target.value)}
                error={!!getFieldError('entityLegalName')}
                helperText={getFieldError('entityLegalName')?.message}
                required
                data-testid={`fincen-party-${transfereeIndex}-${partyIndex}-entity-legal-name-input`}
                fullWidth
              />
            </Grid>
            {(formData.alternateNames || ['']).map((name, idx) => (
              <Grid item xs={12} key={idx}>
                <AgentNetTextInput
                  label="Alternate Name (i.e., trade name, DBA)"
                  name={`alternateName_${idx}`}
                  value={name}
                  variant="outlined"
                  fullWidth
                  error={!!getFieldError('alternateNames')}
                  helperText={getFieldError('alternateNames')?.message}
                  data-testid={`fincen-party-${transfereeIndex}-${partyIndex}-alternate-name-${idx}-input`}
                  onChange={(e) => {
                    const newAlternateNames = [...(formData.alternateNames || [])];
                    newAlternateNames[idx] = e.target.value;
                    const updatedFormData = {
                      ...formData,
                      alternateNames: newAlternateNames,
                    };
                    setFormData(updatedFormData);
                    onUpdate(updatedFormData, transfereeIndex, partyIndex);
                  }}
                />
              </Grid>
            ))}
          </Grid>
        </div>
      )}

      {formData.partyType !== PartyType.TRUSTEE_LEGAL_ENTITY && (
        <div className={classes.sectionContainer}>
          {(formData.partyType === 'beneficialOwner' ||
            formData.partyType === 'bothBeneficialOwnerAndSigningIndividual') && (
            <FormControlLabel
              className={classes.minorCheckboxLabel}
              control={
                <Checkbox
                  checked={formData.isMinor || false}
                  onChange={(e) => {
                    const updatedFormData = { ...formData, isMinor: e.target.checked };
                    setFormData(updatedFormData);
                    onUpdate(updatedFormData, transfereeIndex, partyIndex);
                  }}
                  name="isMinor"
                  data-testid={`fincen-party-${transfereeIndex}-${partyIndex}-is-minor-checkbox`}
                />
              }
              label="Parent/Guardian information instead of minor child"
            />
          )}

          <Grid container spacing={2}>
            <Grid item xs={12} sm={3}>
              <AgentNetTextInput
                label="First Name"
                name="firstName"
                value={formData.firstName}
                onChange={(e) => handleFieldChange('firstName', e.target.value)}
                error={!!getFieldError('firstName')}
                helperText={getFieldError('firstName')?.message}
                required
                data-testid={`fincen-party-${transfereeIndex}-${partyIndex}-firstname-input`}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <AgentNetTextInput
                label="Middle Name"
                name="middleName"
                value={formData.middleName}
                onChange={(e) => handleFieldChange('middleName', e.target.value)}
                error={!!getFieldError('middleName')}
                helperText={getFieldError('middleName')?.message}
                data-testid={`fincen-party-${transfereeIndex}-${partyIndex}-middlename-input`}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <AgentNetTextInput
                label="Last Name"
                name="lastName"
                value={formData.lastName}
                onChange={(e) => handleFieldChange('lastName', e.target.value)}
                error={!!getFieldError('lastName')}
                helperText={getFieldError('lastName')?.message}
                required
                data-testid={`fincen-party-${transfereeIndex}-${partyIndex}-lastname-input`}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <AgentNetTextInput
                label="Suffix"
                name="suffix"
                value={formData.suffix}
                onChange={(e) => handleFieldChange('suffix', e.target.value)}
                error={!!getFieldError('suffix')}
                helperText={getFieldError('suffix')?.message}
                data-testid={`fincen-party-${transfereeIndex}-${partyIndex}-suffix-input`}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <DateFieldString
                label="Date of Birth"
                format="MM/dd/yyyy"
                value={formData.dateOfBirth || ''}
                onChange={handleDateOfBirthChange}
                fullWidth
                variant="outlined"
                qaAttribute={`fincen-party-${transfereeIndex}-${partyIndex}-date-of-birth-input`}
                placeholder="mm/dd/yyyy"
                error={!!getFieldError('dateOfBirth')}
                helperText={getFieldError('dateOfBirth')?.message}
                showValidation={true}
                shouldPrioritizeOnChange={false}
                required
              />
            </Grid>
          </Grid>
        </div>
      )}

      <div className={classes.sectionContainer}>
        <Typography variant="h6" className={classes.sectionHeader}>
          {formData.partyType === PartyType.TRUSTEE_LEGAL_ENTITY ? 'Business Address' : 'Residential Address'}
        </Typography>

        {formData.partyType === PartyType.TRUSTEE_LEGAL_ENTITY && (
          <Box mb={3}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.foreignBusinessNoUSLocation || false}
                  onChange={(e) => {
                    const updatedFormData = {
                      ...formData,
                      foreignBusinessNoUSLocation: e.target.checked,
                      // When turning off foreign business toggle, default addressType to 'foreign' (Principal Place of Business U.S.)
                      // When turning on, clear addressType
                      addressType: e.target.checked ? undefined : formData.addressType || 'foreign',
                    };
                    setFormData(updatedFormData);
                    onUpdate(updatedFormData, transfereeIndex, partyIndex);
                  }}
                  name="foreignBusinessNoUSLocation"
                  className={classes.switch}
                  data-testid={`fincen-party-${transfereeIndex}-${partyIndex}-foreign-business-switch`}
                />
              }
              label={
                <Box display="flex" alignItems="center">
                  <Typography>Foreign principal place of business with no U.S. location</Typography>
                  <Tooltip title="Select this if the business has no U.S. location">
                    <InfoIcon className={classes.infoIcon} />
                  </Tooltip>
                </Box>
              }
            />
          </Box>
        )}

        {formData.partyType === PartyType.TRUSTEE_LEGAL_ENTITY && formData.foreignBusinessNoUSLocation && (
          <div className={classes.addressContainer}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <GoogleAutocompleteSearch
                  label="Address 1"
                  variant="outlined"
                  name={`[${transfereeIndex}].parties[${partyIndex}].address.address1`}
                  value={formData.address?.address1 || ''}
                  onChange={(e) => handleNestedChange('address', 'address1', e.target.value)}
                  errs={effectiveValidationErrors}
                  showValidation={!!getFieldError('address.address1')}
                  error={!!getFieldError('address.address1')}
                  onPlaceSelect={(place: any) => {
                    const next = {
                      address1: place?.address1 ?? '',
                      address2: place?.address2 ?? '',
                      city: place?.city ?? '',
                      postalCode: place?.zipcode ?? '',
                      country: place?.country ?? formData.address?.country,
                    };
                    const updatedFormData = {
                      ...formData,
                      address: {
                        ...formData.address,
                        ...next,
                      },
                    };
                    setFormData(updatedFormData);
                    onUpdate(updatedFormData, transfereeIndex, partyIndex);
                  }}
                  data-testid={`fincen-party-${transfereeIndex}-${partyIndex}-foreign-business-address1-input`}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <AgentNetTextInput
                  label="Address 2"
                  name="address2"
                  value={formData.address?.address2 || ''}
                  onChange={(e) => handleNestedChange('address', 'address2', e.target.value)}
                  error={!!getFieldError('address.address2')}
                  helperText={getFieldError('address.address2')?.message}
                  data-testid={`fincen-party-${transfereeIndex}-${partyIndex}-foreign-business-address2-input`}
                  fullWidth
                />
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={3}>
                <AgentNetTextInput
                  label="City"
                  name="city"
                  value={formData.address?.city || ''}
                  onChange={(e) => handleNestedChange('address', 'city', e.target.value)}
                  error={!!getFieldError('address.city')}
                  helperText={getFieldError('address.city')?.message}
                  data-testid={`fincen-party-${transfereeIndex}-${partyIndex}-foreign-business-city-input`}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={3}>
                <AgentNetDropdownSelector
                  label="Country/Jurisdiction"
                  name={`[${transfereeIndex}].parties[${partyIndex}].address.country`}
                  value={formData.address?.country || ''}
                  menuOption={(value) => {
                    handleCountryChange(value as string);
                    clearFieldError('address.country');
                  }}
                  options={allCountryOptions}
                  data-testid={`fincen-party-${transfereeIndex}-${partyIndex}-foreign-business-country-select`}
                  errs={effectiveValidationErrors}
                  showValidation={formSubmitted || !!generateButtonClicked}
                  error={!!getFieldError('address.country')}
                  variant="outlined"
                  fullWidth
                  required
                />
              </Grid>

              {requiresStateAndPostalCode(formData.address?.country) && (
                <Grid item xs={3}>
                  <AgentNetDropdownSelector
                    label={getStateLabel(formData.address?.country)}
                    name={`[${transfereeIndex}].parties[${partyIndex}].address.state`}
                    value={formData.address?.state || ''}
                    menuOption={(value: string) => {
                      handleStateChange(value);
                      clearFieldError('address.state');
                    }}
                    options={getStateOptions(formData.address?.country)}
                    data-testid={`fincen-party-${transfereeIndex}-${partyIndex}-foreign-business-state-select`}
                    errs={effectiveValidationErrors}
                    showValidation={formSubmitted || !!generateButtonClicked}
                    error={!!getFieldError('address.state')}
                    variant="outlined"
                    fullWidth
                    required
                    disabled={isUSTerritory(formData.address?.country)}
                  />
                </Grid>
              )}

              <Grid item xs={3}>
                {isUS(formData.address?.country) || isUSTerritory(formData.address?.country) ? (
                  <USPostalCodeField
                    label="Zip/Postal Code"
                    name="postalCode"
                    value={formData.address?.postalCode || ''}
                    onChange={(e) => handleNestedChange('address', 'postalCode', e.target.value)}
                    error={!!getFieldError('address.postalCode')}
                    helperText={getFieldError('address.postalCode')?.message}
                    data-testid={`fincen-party-${transfereeIndex}-${partyIndex}-foreign-business-postal-code-input`}
                    fullWidth
                    required={requiresStateAndPostalCode(formData.address?.country)}
                  />
                ) : (
                  <AgentNetTextInput
                    label="Zip/Postal Code"
                    name="postalCode"
                    value={formData.address?.postalCode || ''}
                    onChange={(e) => handleNestedChange('address', 'postalCode', e.target.value)}
                    error={!!getFieldError('address.postalCode')}
                    helperText={getFieldError('address.postalCode')?.message}
                    data-testid={`fincen-party-${transfereeIndex}-${partyIndex}-foreign-business-postal-code-input`}
                    fullWidth
                    required={requiresStateAndPostalCode(formData.address?.country)}
                    inputProps={{ maxLength: 9 }}
                  />
                )}
              </Grid>
            </Grid>
          </div>
        )}

        {formData.partyType === PartyType.TRUSTEE_LEGAL_ENTITY && !formData.foreignBusinessNoUSLocation && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <AgentNetDropdownSelector
                label="Address Type"
                name={`[${transfereeIndex}].parties[${partyIndex}].addressType`}
                value={formData.addressType || 'foreign'}
                menuOption={(value) => {
                  const updatedFormData = { ...formData, addressType: value as string };
                  setFormData(updatedFormData);
                  onUpdate(updatedFormData, transfereeIndex, partyIndex);
                  clearFieldError('addressType');
                }}
                options={addressTypeOptions}
                data-testid={`fincen-party-${transfereeIndex}-${partyIndex}-address-type-select`}
                error={!!getFieldError('addressType')}
                className={getFieldError('addressType') ? classes.dropdownError : ''}
                variant="outlined"
                fullWidth
                style={{ textAlign: 'left' }}
                defaultToFirst={false}
                required
              />
              {getFieldError('addressType') && (
                <Typography variant="caption" color="error" className={classes.errorMessage}>
                  {getFieldError('addressType')?.message}
                </Typography>
              )}
            </Grid>
          </Grid>
        )}

        {formData.partyType === PartyType.TRUSTEE_LEGAL_ENTITY &&
          !formData.foreignBusinessNoUSLocation &&
          formData.addressType === 'primary' && (
            <>
              <Typography variant="h6" className={classes.sectionHeader}>
                Foreign Principal Place of Business
              </Typography>
              <Grid container spacing={2} style={{ marginBottom: '24px' }}>
                <Grid item xs={12} sm={6}>
                  <GoogleAutocompleteSearch
                    label="Address 1"
                    variant="outlined"
                    name={`[${transfereeIndex}].parties[${partyIndex}].foreignPrincipalAddress.address1`}
                    value={formData.foreignPrincipalAddress?.address1 || ''}
                    onChange={(e) => handleNestedChange('foreignPrincipalAddress', 'address1', e.target.value)}
                    errs={effectiveValidationErrors}
                    showValidation={!!getFieldError('foreignPrincipalAddress.address1')}
                    onPlaceSelect={(place: any) => {
                      const next = {
                        address1: place?.address1 ?? '',
                        address2: place?.address2 ?? '',
                        city: place?.city ?? '',
                        postalCode: place?.zipcode ?? '',
                        country: place?.country ?? formData.foreignPrincipalAddress?.country,
                      };
                      const updatedFormData = {
                        ...formData,
                        foreignPrincipalAddress: {
                          ...formData.foreignPrincipalAddress,
                          ...next,
                        },
                      };
                      setFormData(updatedFormData);
                      onUpdate(updatedFormData, transfereeIndex, partyIndex);
                    }}
                    data-testid={`fincen-party-${transfereeIndex}-${partyIndex}-foreign-principal-address1-input`}
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item xs={6}>
                  <AgentNetTextInput
                    label="Address 2"
                    name="address2"
                    value={formData.foreignPrincipalAddress?.address2 || ''}
                    onChange={(e) => handleNestedChange('foreignPrincipalAddress', 'address2', e.target.value)}
                    error={!!getFieldError('foreignPrincipalAddress.address2')}
                    helperText={getFieldError('foreignPrincipalAddress.address2')?.message}
                    data-testid={`fincen-party-${transfereeIndex}-${partyIndex}-foreign-principal-address2-input`}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <AgentNetTextInput
                    label="City"
                    name="city"
                    value={formData.foreignPrincipalAddress?.city || ''}
                    onChange={(e) => handleNestedChange('foreignPrincipalAddress', 'city', e.target.value)}
                    error={!!getFieldError('foreignPrincipalAddress.city')}
                    helperText={getFieldError('foreignPrincipalAddress.city')?.message}
                    data-testid={`fincen-party-${transfereeIndex}-${partyIndex}-foreign-city-input`}
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <AgentNetDropdownSelector
                    label="Country/Jurisdiction"
                    name={`[${transfereeIndex}].parties[${partyIndex}].foreignPrincipalAddress.country`}
                    value={formData.foreignPrincipalAddress?.country || ''}
                    menuOption={(value) => {
                      handleForeignCountryChange(value as string);
                      clearFieldError('foreignPrincipalAddress.country');
                    }}
                    data-testid={`fincen-party-${transfereeIndex}-${partyIndex}-foreign-country-select`}
                    errs={effectiveValidationErrors}
                    showValidation={formSubmitted || !!generateButtonClicked}
                    error={!!getFieldError('foreignPrincipalAddress.country')}
                    variant="outlined"
                    fullWidth
                    options={allCountryOptions}
                    required
                  />
                </Grid>
                {requiresStateAndPostalCode(formData.foreignPrincipalAddress?.country) && (
                  <Grid item xs={12} sm={3}>
                    <AgentNetDropdownSelector
                      label={getStateLabel(formData.foreignPrincipalAddress?.country)}
                      name={`[${transfereeIndex}].parties[${partyIndex}].foreignPrincipalAddress.state`}
                      value={formData.foreignPrincipalAddress?.state || ''}
                      menuOption={(value: string) => {
                        const newFormData = {
                          ...formData,
                          foreignPrincipalAddress: {
                            ...formData.foreignPrincipalAddress,
                            state: value,
                          },
                        };
                        setFormData(newFormData);
                        onUpdate(newFormData, transfereeIndex, partyIndex);
                        clearFieldError('foreignPrincipalAddress.state');
                      }}
                      options={getStateOptions(formData.foreignPrincipalAddress?.country)}
                      data-testid={`fincen-party-${transfereeIndex}-${partyIndex}-foreign-state-select`}
                      errs={effectiveValidationErrors}
                      showValidation={formSubmitted || !!generateButtonClicked}
                      error={!!getFieldError('foreignPrincipalAddress.state')}
                      variant="outlined"
                      fullWidth
                      required
                      disabled={isUSTerritory(formData.foreignPrincipalAddress?.country)}
                    />
                  </Grid>
                )}
                <Grid item xs={12} sm={3}>
                  {isUS(formData.foreignPrincipalAddress?.country) ||
                  isUSTerritory(formData.foreignPrincipalAddress?.country) ? (
                    <USPostalCodeField
                      label="Zip/Postal Code"
                      name="foreignPostalCode"
                      value={formData.foreignPrincipalAddress?.postalCode || ''}
                      onChange={(e) => {
                        const newFormData = {
                          ...formData,
                          foreignPrincipalAddress: {
                            ...formData.foreignPrincipalAddress,
                            postalCode: e.target.value,
                          },
                        };
                        setFormData(newFormData);
                        onUpdate(newFormData, transfereeIndex, partyIndex);
                      }}
                      error={!!getFieldError('foreignPrincipalAddress.postalCode')}
                      helperText={getFieldError('foreignPrincipalAddress.postalCode')?.message}
                      data-testid={`fincen-party-${transfereeIndex}-${partyIndex}-foreign-postal-code-input`}
                      fullWidth
                      required={requiresStateAndPostalCode(formData.foreignPrincipalAddress?.country)}
                    />
                  ) : (
                    <AgentNetTextInput
                      label="Zip/Postal Code"
                      name="foreignPostalCode"
                      value={formData.foreignPrincipalAddress?.postalCode || ''}
                      onChange={(e) => {
                        const newFormData = {
                          ...formData,
                          foreignPrincipalAddress: {
                            ...formData.foreignPrincipalAddress,
                            postalCode: e.target.value,
                          },
                        };
                        setFormData(newFormData);
                        onUpdate(newFormData, transfereeIndex, partyIndex);
                      }}
                      error={!!getFieldError('foreignPrincipalAddress.postalCode')}
                      helperText={getFieldError('foreignPrincipalAddress.postalCode')?.message}
                      data-testid={`fincen-party-${transfereeIndex}-${partyIndex}-foreign-postal-code-input`}
                      fullWidth
                      required={requiresStateAndPostalCode(formData.foreignPrincipalAddress?.country)}
                      inputProps={{ maxLength: 9 }}
                    />
                  )}
                </Grid>
              </Grid>

              <Typography variant="h6" className={classes.sectionHeader}>
                Primary U.S. Location
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <GoogleAutocompleteSearch
                    label="Address 1"
                    variant="outlined"
                    name={`[${transfereeIndex}].parties[${partyIndex}].address.address1`}
                    value={formData.address?.address1 || ''}
                    onChange={(e) => handleNestedChange('address', 'address1', e.target.value)}
                    errs={effectiveValidationErrors}
                    showValidation={!!getFieldError('address.address1')}
                    stateConstraint={addressStateConstraint}
                    onPlaceSelect={(place: any) => {
                      const isUSCountry = isUS(formData.address?.country);
                      const next = {
                        address1: place?.address1 ?? '',
                        address2: place?.address2 ?? '',
                        city: place?.city ?? '',
                        postalCode: place?.zipcode ?? '',
                        state: isUSCountry ? place?.state ?? formData.address?.state ?? '' : formData.address?.state,
                      };
                      const updatedFormData = {
                        ...formData,
                        address: {
                          ...formData.address,
                          ...next,
                        },
                      };
                      setFormData(updatedFormData);
                      onUpdate(updatedFormData, transfereeIndex, partyIndex);
                    }}
                    data-testid={`fincen-party-${transfereeIndex}-${partyIndex}-address1-input`}
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item xs={6}>
                  <AgentNetTextInput
                    label="Address 2"
                    name="address2"
                    value={formData.address?.address2 || ''}
                    onChange={(e) => handleNestedChange('address', 'address2', e.target.value)}
                    error={!!getFieldError('address.address2')}
                    helperText={getFieldError('address.address2')?.message}
                    data-testid={`fincen-party-${transfereeIndex}-${partyIndex}-address2-input`}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <AgentNetTextInput
                    label="City"
                    name="city"
                    value={formData.address?.city || ''}
                    onChange={(e) => handleNestedChange('address', 'city', e.target.value)}
                    error={!!getFieldError('address.city')}
                    helperText={getFieldError('address.city')?.message}
                    data-testid={`fincen-party-${transfereeIndex}-${partyIndex}-city-input`}
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <AgentNetDropdownSelector
                    label="Country/Jurisdiction"
                    name={`[${transfereeIndex}].parties[${partyIndex}].address.country`}
                    value={formData.address?.country || ''}
                    menuOption={(value) => {
                      handleCountryChange(value as string);
                      clearFieldError('address.country');
                    }}
                    data-testid={`fincen-party-${transfereeIndex}-${partyIndex}-country-select`}
                    errs={effectiveValidationErrors}
                    showValidation={formSubmitted || !!generateButtonClicked}
                    error={!!getFieldError('address.country')}
                    variant="outlined"
                    fullWidth
                    options={allCountryOptions}
                    required
                  />
                </Grid>
                {requiresStateAndPostalCode(formData.address?.country) && (
                  <Grid item xs={12} sm={3}>
                    <AgentNetDropdownSelector
                      label={getStateLabel(formData.address?.country)}
                      name={`[${transfereeIndex}].parties[${partyIndex}].address.state`}
                      value={formData.address?.state || ''}
                      menuOption={(value: string) => {
                        handleStateChange(value);
                        clearFieldError('address.state');
                      }}
                      options={getStateOptions(formData.address?.country)}
                      data-testid={`fincen-party-${transfereeIndex}-${partyIndex}-state-select`}
                      errs={effectiveValidationErrors}
                      showValidation={formSubmitted || !!generateButtonClicked}
                      error={!!getFieldError('address.state')}
                      variant="outlined"
                      fullWidth
                      required
                      disabled={isUSTerritory(formData.address?.country)}
                    />
                  </Grid>
                )}
                <Grid item xs={12} sm={3}>
                  {isUS(formData.address?.country) || isUSTerritory(formData.address?.country) ? (
                    <USPostalCodeField
                      label="Zip/Postal Code"
                      name="postalCode"
                      value={formData.address?.postalCode || ''}
                      onChange={(e) => handleNestedChange('address', 'postalCode', e.target.value)}
                      error={!!getFieldError('address.postalCode')}
                      helperText={getFieldError('address.postalCode')?.message}
                      data-testid={`fincen-party-${transfereeIndex}-${partyIndex}-postal-code-input`}
                      fullWidth
                      required={requiresStateAndPostalCode(formData.address?.country)}
                    />
                  ) : (
                    <AgentNetTextInput
                      label="Zip/Postal Code"
                      name="postalCode"
                      value={formData.address?.postalCode || ''}
                      onChange={(e) => handleNestedChange('address', 'postalCode', e.target.value)}
                      error={!!getFieldError('address.postalCode')}
                      helperText={getFieldError('address.postalCode')?.message}
                      data-testid={`fincen-party-${transfereeIndex}-${partyIndex}-postal-code-input`}
                      fullWidth
                      required={requiresStateAndPostalCode(formData.address?.country)}
                      inputProps={{ maxLength: 9 }}
                    />
                  )}
                </Grid>
              </Grid>
            </>
          )}

        {formData.partyType === PartyType.TRUSTEE_LEGAL_ENTITY &&
          !formData.foreignBusinessNoUSLocation &&
          (formData.addressType || 'foreign') === 'foreign' && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <GoogleAutocompleteSearch
                  label="Address 1"
                  variant="outlined"
                  name={`[${transfereeIndex}].parties[${partyIndex}].address.address1`}
                  value={formData.address?.address1 || ''}
                  onChange={(e) => handleNestedChange('address', 'address1', e.target.value)}
                  errs={effectiveValidationErrors}
                  showValidation={!!getFieldError('address.address1')}
                  stateConstraint={addressStateConstraint}
                  onPlaceSelect={(place: any) => {
                    const isUSCountry = isUS(formData.address?.country);
                    const next = {
                      address1: place?.address1 ?? '',
                      address2: place?.address2 ?? '',
                      city: place?.city ?? '',
                      postalCode: place?.zipcode ?? '',
                      state: isUSCountry ? place?.state ?? formData.address?.state ?? '' : formData.address?.state,
                    };
                    const updatedFormData = {
                      ...formData,
                      address: {
                        ...formData.address,
                        ...next,
                      },
                    };
                    setFormData(updatedFormData);
                    onUpdate(updatedFormData, transfereeIndex, partyIndex);
                  }}
                  data-testid={`fincen-party-${transfereeIndex}-${partyIndex}-address1-input`}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <AgentNetTextInput
                  label="Address 2"
                  name="address2"
                  value={formData.address?.address2 || ''}
                  onChange={(e) => handleNestedChange('address', 'address2', e.target.value)}
                  error={!!getFieldError('address.address2')}
                  helperText={getFieldError('address.address2')?.message}
                  data-testid={`fincen-party-${transfereeIndex}-${partyIndex}-address2-input`}
                  fullWidth
                />
              </Grid>
              <Grid
                item
                xs={12}
                sm={
                  formData.partyType === PartyType.TRUSTEE_LEGAL_ENTITY && formData.foreignBusinessNoUSLocation ? 4 : 3
                }
              >
                <AgentNetTextInput
                  label="City"
                  name="city"
                  value={formData.address?.city || ''}
                  onChange={(e) => handleNestedChange('address', 'city', e.target.value)}
                  error={!!getFieldError('address.city')}
                  helperText={getFieldError('address.city')?.message}
                  data-testid={`fincen-party-${transfereeIndex}-${partyIndex}-city-input`}
                  fullWidth
                  required
                />
              </Grid>
              <Grid
                item
                xs={12}
                sm={
                  formData.partyType === PartyType.TRUSTEE_LEGAL_ENTITY && formData.foreignBusinessNoUSLocation ? 4 : 3
                }
              >
                <AgentNetDropdownSelector
                  label="Country/Jurisdiction"
                  name={`[${transfereeIndex}].parties[${partyIndex}].address.country`}
                  value={formData.address?.country || ''}
                  menuOption={(value) => {
                    handleCountryChange(value as string);
                    clearFieldError('address.country');
                  }}
                  data-testid={`fincen-party-${transfereeIndex}-${partyIndex}-country-select`}
                  errs={effectiveValidationErrors}
                  showValidation={formSubmitted || !!generateButtonClicked}
                  variant="outlined"
                  fullWidth
                  options={allCountryOptions}
                  required
                />
              </Grid>
              {!(formData.partyType === PartyType.TRUSTEE_LEGAL_ENTITY && formData.foreignBusinessNoUSLocation) &&
                requiresStateAndPostalCode(formData.address?.country) && (
                  <Grid item xs={12} sm={3}>
                    <AgentNetDropdownSelector
                      label={getStateLabel(formData.address?.country)}
                      name={`[${transfereeIndex}].parties[${partyIndex}].address.state`}
                      value={formData.address?.state || ''}
                      menuOption={(value: string) => {
                        handleStateChange(value);
                        clearFieldError('address.state');
                      }}
                      options={getStateOptions(formData.address?.country)}
                      data-testid={`fincen-party-${transfereeIndex}-${partyIndex}-state-select`}
                      errs={effectiveValidationErrors}
                      showValidation={formSubmitted || !!generateButtonClicked}
                      error={!!getFieldError('address.state')}
                      variant="outlined"
                      fullWidth
                      required
                      disabled={isUSTerritory(formData.address?.country)}
                    />
                  </Grid>
                )}
              <Grid
                item
                xs={12}
                sm={
                  formData.partyType === PartyType.TRUSTEE_LEGAL_ENTITY && formData.foreignBusinessNoUSLocation ? 4 : 3
                }
              >
                {isUS(formData.address?.country) || isUSTerritory(formData.address?.country) ? (
                  <USPostalCodeField
                    label="Zip/Postal Code"
                    name="postalCode"
                    value={formData.address?.postalCode || ''}
                    onChange={(e) => handleNestedChange('address', 'postalCode', e.target.value)}
                    error={!!getFieldError('address.postalCode')}
                    helperText={getFieldError('address.postalCode')?.message}
                    data-testid={`fincen-party-${transfereeIndex}-${partyIndex}-postal-code-input`}
                    fullWidth
                    required={requiresStateAndPostalCode(formData.address?.country)}
                  />
                ) : (
                  <AgentNetTextInput
                    label="Zip/Postal Code"
                    name="postalCode"
                    value={formData.address?.postalCode || ''}
                    onChange={(e) => handleNestedChange('address', 'postalCode', e.target.value)}
                    error={!!getFieldError('address.postalCode')}
                    helperText={getFieldError('address.postalCode')?.message}
                    data-testid={`fincen-party-${transfereeIndex}-${partyIndex}-postal-code-input`}
                    fullWidth
                    required={requiresStateAndPostalCode(formData.address?.country)}
                    inputProps={{ maxLength: 9 }}
                  />
                )}
              </Grid>
            </Grid>
          )}

        {formData.partyType !== PartyType.TRUSTEE_LEGAL_ENTITY && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <GoogleAutocompleteSearch
                label="Address 1"
                variant="outlined"
                name={`[${transfereeIndex}].parties[${partyIndex}].address.address1`}
                value={formData.address?.address1 || ''}
                onChange={(e) => handleNestedChange('address', 'address1', e.target.value)}
                errs={effectiveValidationErrors}
                showValidation={!!getFieldError('address.address1')}
                error={!!getFieldError('address.address1')}
                stateConstraint={addressStateConstraint}
                onPlaceSelect={(place: any) => {
                  const isUSCountry = isUS(formData.address?.country);
                  const next = {
                    address1: place?.address1 ?? '',
                    address2: place?.address2 ?? '',
                    city: place?.city ?? '',
                    postalCode: place?.zipcode ?? '',
                    state: isUSCountry ? place?.state ?? formData.address?.state ?? '' : formData.address?.state,
                  };
                  const updatedFormData = {
                    ...formData,
                    address: {
                      ...formData.address,
                      ...next,
                    },
                  };
                  setFormData(updatedFormData);
                  onUpdate(updatedFormData, transfereeIndex, partyIndex);
                }}
                data-testid={`fincen-party-${transfereeIndex}-${partyIndex}-address1-input`}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={6}>
              <AgentNetTextInput
                label="Address 2"
                name="address2"
                value={formData.address?.address2 || ''}
                onChange={(e) => handleNestedChange('address', 'address2', e.target.value)}
                error={!!getFieldError('address.address2')}
                helperText={getFieldError('address.address2')?.message}
                data-testid={`fincen-party-${transfereeIndex}-${partyIndex}-address2-input`}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <AgentNetTextInput
                label="City"
                name="city"
                value={formData.address?.city || ''}
                onChange={(e) => handleNestedChange('address', 'city', e.target.value)}
                error={!!getFieldError('address.city')}
                helperText={getFieldError('address.city')?.message}
                data-testid={`fincen-party-${transfereeIndex}-${partyIndex}-city-input`}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <AgentNetDropdownSelector
                label="Country/Jurisdiction"
                name={`[${transfereeIndex}].parties[${partyIndex}].address.country`}
                value={formData.address?.country || ''}
                menuOption={(value) => {
                  handleCountryChange(value as string);
                  clearFieldError('address.country');
                }}
                data-testid={`fincen-party-${transfereeIndex}-${partyIndex}-country-select`}
                errs={effectiveValidationErrors}
                showValidation={formSubmitted || !!generateButtonClicked}
                variant="outlined"
                fullWidth
                options={allCountryOptions}
                required
              />
            </Grid>
            {requiresStateAndPostalCode(formData.address?.country) && (
              <Grid item xs={12} sm={3}>
                <AgentNetDropdownSelector
                  label={getStateLabel(formData.address?.country)}
                  name={`[${transfereeIndex}].parties[${partyIndex}].address.state`}
                  value={formData.address?.state || ''}
                  menuOption={(value: string) => {
                    handleStateChange(value);
                    clearFieldError('address.state');
                  }}
                  options={getStateOptions(formData.address?.country)}
                  data-testid={`fincen-party-${transfereeIndex}-${partyIndex}-state-select`}
                  errs={effectiveValidationErrors}
                  showValidation={formSubmitted || !!generateButtonClicked}
                  error={!!getFieldError('address.state')}
                  variant="outlined"
                  fullWidth
                  required
                  disabled={isUSTerritory(formData.address?.country)}
                />
              </Grid>
            )}
            <Grid item xs={12} sm={3}>
              {isUS(formData.address?.country) || isUSTerritory(formData.address?.country) ? (
                <USPostalCodeField
                  label="Zip/Postal Code"
                  name="postalCode"
                  value={formData.address?.postalCode || ''}
                  onChange={(e) => handleNestedChange('address', 'postalCode', e.target.value)}
                  error={!!getFieldError('address.postalCode')}
                  helperText={getFieldError('address.postalCode')?.message}
                  data-testid={`fincen-party-${transfereeIndex}-${partyIndex}-postal-code-input`}
                  fullWidth
                  required={requiresStateAndPostalCode(formData.address?.country)}
                />
              ) : (
                <AgentNetTextInput
                  label="Zip/Postal Code"
                  name="postalCode"
                  value={formData.address?.postalCode || ''}
                  onChange={(e) => handleNestedChange('address', 'postalCode', e.target.value)}
                  error={!!getFieldError('address.postalCode')}
                  helperText={getFieldError('address.postalCode')?.message}
                  data-testid={`fincen-party-${transfereeIndex}-${partyIndex}-postal-code-input`}
                  fullWidth
                  required={requiresStateAndPostalCode(formData.address?.country)}
                  inputProps={{ maxLength: 9 }}
                />
              )}
            </Grid>
          </Grid>
        )}
      </div>

      <div className={classes.formContainer}>
        <Typography variant="h6" className={classes.sectionHeader}>
          Form of Identification and Issuing Jurisdiction
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <AgentNetDropdownSelector
              label="Type of Unique Identifying Number"
              name={`[${transfereeIndex}].parties[${partyIndex}].identificationType`}
              value={formData.identificationType}
              menuOption={(value) => handleFieldChange('identificationType', value as string)}
              options={getIdentificationTypeOptions(formData.partyType)}
              data-testid={`fincen-party-${transfereeIndex}-${partyIndex}-identification-type-select`}
              error={!!getFieldError('identificationType')}
              className={getFieldError('identificationType') ? classes.dropdownError : ''}
              variant="outlined"
              fullWidth
              required
            />
            {getFieldError('identificationType') && (
              <Typography variant="caption" color="error" className={classes.errorMessage}>
                {getFieldError('identificationType')?.message}
              </Typography>
            )}
          </Grid>

          {formData.identificationType && formData.identificationType !== 'noIdentification' && (
            <Grid item xs={12} sm={6}>
              <IdentificationNumberField
                identificationType={formData.identificationType}
                value={formData.identificationNumber || ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleFieldChange('identificationNumber', e.target.value)
                }
                error={!!getFieldError('identificationNumber')}
                helperText={getFieldError('identificationNumber')?.message}
                name="identificationNumber"
                data-testid={`fincen-party-${transfereeIndex}-${partyIndex}-identification-number-input`}
                keyPrefix={`${transfereeIndex}-${partyIndex}`}
                required
                fullWidth
              />
            </Grid>
          )}

          {formData.identificationType &&
            (formData.identificationType === 'foreignTaxId' ||
              formData.identificationType === 'foreignPassport' ||
              formData.identificationType === 'foreignEntityRegistrationNumber') && (
              <Grid item xs={6}>
                <AgentNetDropdownSelector
                  label="Issuing Jurisdiction(if foreign)"
                  name={`[${transfereeIndex}].parties[${partyIndex}].issuingJurisdiction`}
                  value={formData.issuingJurisdiction || ''}
                  menuOption={(value) => {
                    const updatedFormData = { ...formData, issuingJurisdiction: value as string };
                    setFormData(updatedFormData);
                    onUpdate(updatedFormData, transfereeIndex, partyIndex);
                    clearFieldError('issuingJurisdiction');
                  }}
                  options={foreignTerritoryOptions}
                  data-testid={`fincen-party-${transfereeIndex}-${partyIndex}-issuing-jurisdiction-select`}
                  errs={effectiveValidationErrors}
                  showValidation={!!getFieldError('issuingJurisdiction')}
                  error={!!getFieldError('issuingJurisdiction')}
                  className={getFieldError('issuingJurisdiction') ? classes.dropdownError : ''}
                  variant="outlined"
                  required
                  fullWidth
                />
              </Grid>
            )}
        </Grid>
      </div>

      {(formData.partyType === 'signingIndividual' ||
        formData.partyType === 'bothBeneficialOwnerAndSigningIndividual') && (
        <div className={classes.sectionContainer}>
          <Typography variant="h6" className={classes.sectionHeader}>
            Authorization Info
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <AgentNetDropdownSelector
                label="Authorization Capacity"
                name={`[${transfereeIndex}].parties[${partyIndex}].authorizationCapacity`}
                value={formData.authorizationCapacity || ''}
                menuOption={(value) => {
                  const updatedFormData = { ...formData, authorizationCapacity: value as string };
                  setFormData(updatedFormData);
                  onUpdate(updatedFormData, transfereeIndex, partyIndex);
                  clearFieldError('authorizationCapacity');
                }}
                options={authorizationCapacityOptions}
                data-testid={`fincen-party-${transfereeIndex}-${partyIndex}-authorization-capacity-select`}
                errs={effectiveValidationErrors}
                showValidation={formSubmitted || !!generateButtonClicked}
                variant="outlined"
                fullWidth
              />
            </Grid>
            {formData.authorizationCapacity === 'other' ? (
              <Grid item xs={12} sm={6}>
                <AgentNetTextInput
                  label="Other Description"
                  name="authorizationOtherDescription"
                  value={formData.authorizationOtherDescription || ''}
                  onChange={(e) => handleFieldChange('authorizationOtherDescription', e.target.value)}
                  error={!!getFieldError('authorizationOtherDescription')}
                  helperText={getFieldError('authorizationOtherDescription')?.message}
                  data-testid={`fincen-party-${transfereeIndex}-${partyIndex}-authorization-other-description-input`}
                  required
                  fullWidth
                />
              </Grid>
            ) : (
              <Grid item xs={12} sm={6}>
                <AgentNetTextInput
                  label="Name of Employer, Principal,Partnership, etc."
                  name="employerName"
                  value={formData.employerName || ''}
                  onChange={(e) => handleFieldChange('employerName', e.target.value)}
                  error={!!getFieldError('employerName')}
                  helperText={getFieldError('employerName')?.message}
                  data-testid={`fincen-party-${transfereeIndex}-${partyIndex}-employer-name-input`}
                  required={!!formData.authorizationCapacity && formData.authorizationCapacity !== 'other'}
                  fullWidth
                />
              </Grid>
            )}
          </Grid>
        </div>
      )}

      {(formData.partyType === 'beneficialOwner' ||
        formData.partyType === 'bothBeneficialOwnerAndSigningIndividual') && (
        <div className={classes.sectionContainer}>
          <Typography variant="h6" className={classes.sectionHeader}>
            Beneficial Owner Info
          </Typography>

          <Grid container spacing={2}>
            {transfereeType !== TransfereeType.ENTITY && (
              <Grid item xs={12} sm={6}>
                <AgentNetDropdownSelector
                  label="Beneficial Owner Category"
                  name={`[${transfereeIndex}].parties[${partyIndex}].beneficialOwnerCategory`}
                  value={formData.beneficialOwnerCategory || ''}
                  menuOption={(value) => {
                    const updatedFormData = {
                      ...formData,
                      beneficialOwnerCategory: value as string,
                    };
                    setFormData(updatedFormData);
                    onUpdate(updatedFormData, transfereeIndex, partyIndex);
                    clearFieldError('beneficialOwnerCategory');
                  }}
                  options={beneficialOwnerCategoryOptions}
                  data-testid={`fincen-party-${transfereeIndex}-${partyIndex}-beneficial-owner-category-select`}
                  errs={effectiveValidationErrors}
                  showValidation={formSubmitted || !!generateButtonClicked}
                  error={!!getFieldError('beneficialOwnerCategory')}
                  className={getFieldError('beneficialOwnerCategory') ? classes.dropdownError : ''}
                  variant="outlined"
                  required
                  fullWidth
                />
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <AgentNetDropdownSelector
                label="Country/Jurisdiction of Citizenship (select all that apply)"
                name={`[${transfereeIndex}].parties[${partyIndex}].citizenshipCountry`}
                value={formData.citizenshipCountry || [CountryCode.US]}
                menuOption={(value) => {
                  const updatedFormData = {
                    ...formData,
                    citizenshipCountry: value as string[],
                  };
                  setFormData(updatedFormData);
                  onUpdate(updatedFormData, transfereeIndex, partyIndex);
                  clearFieldError('citizenshipCountry');
                }}
                options={foreignTerritoryOptions}
                data-testid={`fincen-party-${transfereeIndex}-${partyIndex}-citizenship-country-select`}
                errs={effectiveValidationErrors}
                showValidation={formSubmitted || !!generateButtonClicked}
                variant="outlined"
                fullWidth
                required
                multiple
              />
            </Grid>
          </Grid>
        </div>
      )}
    </div>
  );
};

export default PartyForm;
